import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { AdminLayout } from './admin/AdminLayout';
import { CallProvider, useCall } from './call/CallContext';
import { ChatPage } from './chat/ChatPage';
import { LoginPage } from './LoginPage';
import { activateUpdate, callBlocksUpdate, registerPwa } from './pwa';
import { Button, Skeleton } from './ui';

type Route = { area: 'chat' } | { area: 'admin'; page: string };

function parseRoute(): Route {
  const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
  if (!path || path === 'chat') return { area: 'chat' };
  if (path.startsWith('owner')) return { area: 'admin', page: path.split('/')[1] || 'overview' };
  return { area: 'chat' };
}

function useRoute() {
  const [route, setRoute] = useState<Route>(parseRoute);
  useEffect(() => {
    const listener = () => setRoute(parseRoute());
    window.addEventListener('popstate', listener);
    return () => window.removeEventListener('popstate', listener);
  }, []);
  function navigate(path: string) {
    window.history.pushState(null, '', path);
    setRoute(parseRoute());
  }
  return { route, navigate };
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
}

function AppFrame({ route, navigate, onLogout }: {
  route: Route;
  navigate: (path: string) => void;
  onLogout: () => void;
}) {
  const call = useCall();
  const [theme, setTheme] = useState(() => localStorage.getItem('rami-theme') || 'system');
  const [update, setUpdate] = useState<ServiceWorkerRegistration | null>(null);
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const isChat = route.area === 'chat';

  useEffect(() => {
    localStorage.setItem('rami-theme', theme);
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const apply = () => {
      const resolved = theme === 'system' ? (media.matches ? 'light' : 'dark') : theme;
      document.documentElement.dataset.theme = resolved;
      document.documentElement.style.colorScheme = resolved;
    };
    apply();
    if (theme !== 'system') return undefined;
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [theme]);
  useEffect(() => { void registerPwa(setUpdate); }, []);
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    const onInstall = (event: Event) => { event.preventDefault(); setInstallEvent(event as BeforeInstallPromptEvent); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    window.addEventListener('beforeinstallprompt', onInstall);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('beforeinstallprompt', onInstall);
    };
  }, []);

  const callActive = callBlocksUpdate(call.status);
  const themeLabel = useMemo(
    () => (theme === 'system' ? 'حسب الجهاز' : theme === 'dark' ? 'داكن' : 'فاتح'),
    [theme]
  );

  return <div className="app-shell">
    <header className="topbar">
      <button className="brand-button" onClick={() => navigate('/')} aria-label="العودة إلى المحادثة"><span aria-hidden="true">ر</span><strong>رامي</strong></button>
      <div className="topbar__actions">
        {installEvent ? <Button variant="ghost" onClick={() => { void installEvent.prompt(); setInstallEvent(null); }}>تثبيت التطبيق</Button> : null}
        <button className="icon-button theme-button" onClick={() => setTheme(theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system')} aria-label={'المظهر: ' + themeLabel}>◐</button>
        <Button variant="ghost" onClick={() => navigate(isChat ? '/owner/overview' : '/')}>{isChat ? 'لوحة المالك' : 'الحديث'}</Button>
        <Button variant="ghost" onClick={onLogout}>خروج</Button>
      </div>
    </header>
    {!online ? <div className="notice notice--warning" role="status">
      <span>لا يوجد اتصال بالشبكة الآن. تُعرض الحالة المتاحة، وتعود المزامنة عند رجوع الاتصال.</span>
      <Button variant="secondary" onClick={() => window.location.reload()}>إعادة المحاولة</Button>
    </div> : null}
    {update ? <div className="update-banner" role="status">
      <span>{callActive ? 'يتوفر تحديث للواجهة، لكنه لن يُفعَّل أثناء مكالمة أو تسجيل. سيبقى جاهزًا لوقت آمن.' : 'يتوفر تحديث للواجهة. التفعيل لا يفقد جلستك.'}</span>
      <Button variant="secondary" disabled={callActive} onClick={() => { activateUpdate(update); setUpdate(null); }}>
        {callActive ? 'التحديث مؤجّل' : 'تحديث الآن'}
      </Button>
    </div> : null}
    {isChat ? <ChatPage /> : <AdminLayout activePage={route.page} navigate={navigate} />}
  </div>;
}

function AppShell({ onLogout }: { onLogout: () => void }) {
  const { route, navigate } = useRoute();
  return <CallProvider>
    <AppFrame route={route} navigate={navigate} onLogout={onLogout} />
  </CallProvider>;
}

export function App() {
  const [auth, setAuth] = useState<'checking' | 'authenticated' | 'anonymous'>('checking');
  useEffect(() => {
    let active = true;
    api.sessions().then(() => { if (active) setAuth('authenticated'); }).catch(() => { if (active) setAuth('anonymous'); });
    return () => { active = false; };
  }, []);

  if (auth === 'checking') return <main className="boot"><div className="brand-mark" aria-hidden="true">ر</div><Skeleton lines={2} /></main>;
  if (auth === 'anonymous') return <LoginPage onAuthenticated={() => setAuth('authenticated')} />;
  return <AppShell onLogout={() => { void api.logout().finally(() => setAuth('anonymous')); }} />;
}
